import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ATTACHMENTS_HANDLER_CONFIG } from '../config/attachments-handler.config';
import type { FilesystemFilterConfig } from '../models/api/api-responses.model';
import type { FileValidationResult } from '../models/filesystem-filter.model';


@Injectable({
    providedIn: 'root',
})
export class FilesystemFilterService {
    private readonly http = inject(HttpClient);
    private readonly baseUrl = inject(ATTACHMENTS_HANDLER_CONFIG).apiUrl;

    private readonly _config = signal<FilesystemFilterConfig | null>(null);

    readonly config = computed(() => this._config());
    readonly whitelistExtensions = computed(() => this._config()?.whitelist_extensions ?? []);
    readonly blacklistExtensions = computed(() => this._config()?.blacklist_extensions ?? []);
    readonly whitelistMimes = computed(() => this._config()?.whitelist_mimes ?? []);
    readonly blacklistMimes = computed(() => this._config()?.blacklist_mimes ?? []);
    readonly fileMaximumSizes = computed(() => this._config()?.file_maximum_sizes ?? {});

    readonly acceptAttribute = computed<string | null>(() => {
        const config = this._config();
        if (!config) return null;
        const extensions = config.whitelist_extensions.map((ext) => `.${ext}`);
        const mimes = config.whitelist_mimes;
        const tokens = [...extensions, ...mimes];
        return tokens.length > 0 ? tokens.join(',') : null;
    });

    load(): void {
        this.http
            .get<FilesystemFilterConfig>(`${this.baseUrl}filesystem-manager/filesystem-filter/configuration`)
            .subscribe({
                next: (data) => this._config.set(data),
                error: (err) => console.error('Failed to load filesystem filter configuration:', err),
            });
    }

    getMaxSizeFor(mimeType: string): number | undefined {
        return this._config()?.file_maximum_sizes?.[mimeType];
    }

    isMimeAllowed(mimeType: string): boolean {
        const config = this._config();
        if (!config) return true;
        if (config.blacklist_mimes.includes(mimeType)) return false;
        if (config.whitelist_mimes.length === 0) return true;
        return config.whitelist_mimes.includes(mimeType);
    }

    isExtensionAllowed(extension: string): boolean {
        const config = this._config();
        if (!config) return true;
        const ext = extension.toLowerCase().replace(/^\./, '');
        if (config.blacklist_extensions.includes(ext)) return false;
        if (config.whitelist_extensions.length === 0) return true;
        return config.whitelist_extensions.includes(ext);
    }

    validateFile(file: File): FileValidationResult {
        const config = this._config();
        if (!config) return { ok: true };

        const ext = (file.name.split('.').pop() ?? '').toLowerCase();
        const mime = file.type;

        if (ext && config.blacklist_extensions.includes(ext)) {
            return { ok: false, reason: 'extension' };
        }
        if (mime && config.blacklist_mimes.includes(mime)) {
            return { ok: false, reason: 'mime' };
        }

        const hasWhitelist =
            config.whitelist_extensions.length > 0 || config.whitelist_mimes.length > 0;
        if (hasWhitelist) {
            const extOk = ext.length > 0 && config.whitelist_extensions.includes(ext);
            const mimeOk = mime.length > 0 && config.whitelist_mimes.includes(mime);
            if (!extOk && !mimeOk) {
                return { ok: false, reason: 'extension' };
            }
        }

        if (mime) {
            const maxSize = config.file_maximum_sizes[mime];
            if (maxSize !== undefined && file.size > maxSize) {
                return { ok: false, reason: 'size', maxSize };
            }
        }

        return { ok: true };
    }
}
